import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { getPinHistory } from '../../../../redux/actions/unit';
import {
  HeaderText,
  ModalBodyContent,
  ModalHeader,
  StyledModalBody,
  StyledModalV2
} from '../../../../components/Modals';
import LoadingAvatar from '../../../../components/LoadingAvatar';
import Table from '../../../../components/Table';
import { requestPending } from '../../../../utils/status';
import { GET_PIN_HISTORY } from '../../../../redux/constants';
import { LOADING_BLUE } from '../../../../utils/colorCode';
import { closeActiveModal } from '../../../../helper/modals';
import { DATE_FORMATS } from '../../../users/constant';
import { PIN_TYPE_LABELS } from '../../../../utils/lockInfo';

// data: { unitId, label } or { suiteId, label }
const PinHistoryModal = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { modal, data } = useSelector((state) => state.headerModal);
  const building = useSelector((state) => state.building.building);
  const selectedBuilding = useSelector((state) => state.building.globalSelectedBuilding);
  const globalOrgId = useSelector((state) => state.Organization.globalOrgId);
  const { pinHistory, pinHistoryStatus, pinHistoryError } = useSelector((state) => state.unit);

  const orgId = building?.org_id || globalOrgId;
  const buildingId = building?.id || selectedBuilding?.id;

  const fields = useMemo(
    () => [
      { key: 'CreateDate', label: t('Date'), sorter: false, filter: false },
      { key: 'Serial', label: t('Serial number'), sorter: false, filter: false },
      { key: 'TypeID', label: t('Type'), sorter: false, filter: false },
      { key: 'Code', label: t('PIN'), sorter: false, filter: false }
    ],
    [t]
  );

  useEffect(() => {
    if ((data?.unitId || data?.suiteId) && orgId && buildingId) {
      dispatch(
        getPinHistory({
          params: {
            o: orgId,
            b: buildingId,
            ...(data.suiteId ? { suite_id: data.suiteId } : { unit_id: data.unitId })
          }
        })
      );
    }
  }, [data?.unitId, data?.suiteId, orgId, buildingId]);

  const content = useMemo(() => {
    if (pinHistoryError) {
      return <HeaderText alt>{t('Error fetching PIN history')}</HeaderText>;
    }

    if (!pinHistory?.length) {
      return <HeaderText alt>{t('No PIN history found')}</HeaderText>;
    }

    return (
      <Table
        useNewStyle
        items={pinHistory}
        fields={fields}
        scopedSlots={{
          CreateDate: (item) => (
            <td>
              {item.CreateDate
                ? moment(item.CreateDate).local().format(DATE_FORMATS.MM_DD_YYYY_h_mm_A)
                : ' - '}
            </td>
          ),
          Serial: (item) => <td>{item.Serial || ' - '}</td>,
          TypeID: (item) => (
            <td>{`${t(PIN_TYPE_LABELS[item.TypeID] || PIN_TYPE_LABELS[3])} ${item.Slot + 1}`}</td>
          ),
          Code: (item) => <td>{item.Code}</td>
        }}
      />
    );
  }, [pinHistory, pinHistoryError, fields]);

  return (
    <StyledModalV2
      show={modal === 'PinHistoryModal'}
      onClose={closeActiveModal}
      closeOnBackdrop={true}
      width={'650px'}
      side>
      <StyledModalBody>
        <ModalHeader v2 title={`${t('PIN History')} - ${data?.label ?? ''}`} />
        {pinHistoryStatus === requestPending(GET_PIN_HISTORY) ? (
          <LoadingAvatar color={LOADING_BLUE} />
        ) : (
          <ModalBodyContent>{content}</ModalBodyContent>
        )}
      </StyledModalBody>
    </StyledModalV2>
  );
};

export default PinHistoryModal;
